<template>
    <!-- *********************************************
    *                  Quotation Block               *
    ********************************************** -->
    <divider-generic :title="t('bankassurance.subscribe.auto.step2.quotationTitle', { number: getSimulationData.quotation.id})" :applyBorder="false"/>
    <infobox-generic :label="t('bankassurance.subscribe.auto.step2.dateOfBirthInformation')"/>
    <div class="information-wrapper">
        <!-- HolderName -->
        <text-block-generic
        :params="{ label: t('bankassurance.subscribe.auto.step2.nameLabel'), content: titleCaseBuilder(getHolderData.name) }"/>
        <!-- Birthdate -->
        <text-block-generic
        :params="{
            classes: 'capitalize',
            label: t('bankassurance.subscribe.auto.step2.birthDateLabel'),
            content: getDate(formData.birthDate)
        }"/>
        <!-- Travel Protection -->
        <text-block-generic
        :params="{ label: t('bankassurance.subscribe.auto.step2.travelProtectionLabel'), content: t('bankassurance.subscribe.auto.step2.packageValue') }"/>
    </div>
    <button-tertiary
    icon="download"
    buttonSize="small"
    :label="t('bankassurance.subscribe.auto.step2.downloadLabel')"
    @clicked="downloadLink = createDownloadInsuranceUrl(getSimulationData.insuranceId)"/>
    <!-- *********************************************
    *                  Premiums Block                *
    ********************************************** -->
    <divider-generic :title="t('bankassurance.subscribe.auto.step2.premiumsTitle')"/>
    <!-- Table Web -->
    <table-generic
    v-if="!getMobileAppState"
    :hasDetail="false"
    :tableStyles="tableStyles"
    :headerData="headerData"
    :tableData="tableData"/>
    <!-- Mobile Table -->
    <div v-else class="mobile-table-wrapper">
        <div
        class="row"
        v-for="risk in getSimulationData.quotation.premium.policyRisks"
        :key="risk">
            <div class="title-line">{{ firstCharOfStringUppercase(risk.descriptive) }}</div>
            <div class="data-line">{{ `${t('bankassurance.subscribe.auto.step2.tableHeader.capital')}: ${amountFormat(risk.capital.value, risk.capital.currency)}` }}</div>
        </div>
    </div>
    <!-- *********************************************
    *                    Total Block                 *
    ********************************************** -->
    <divider-generic :title="t('bankassurance.subscribe.auto.step2.totalBlockTitle')"/>
    <text-block-generic
    :params="{
        label: t('bankassurance.subscribe.auto.step2.total'),
        content: amountFormat(getSimulationData.quotation.premium.anualSimulatedValue, getSimulationData.quotation.premium.currency)
    }"/>
    <infobox-generic :label="t('bankassurance.subscribe.auto.step2.premiumInformation')"/>
    <!-- Component to handle download logic-->
	<download-logic-component :downloadLink="downloadLink" @clearDownloadLink="downloadLink = null"/>
</template>

<script setup>
import DividerGeneric from '@/components/generic/dividers/Generic'
import InfoboxGeneric from '@/components/generic/infobox/Generic'
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'
import ButtonTertiary from '@/components/generic/buttons/Tertiary'
import TableGeneric from '@/components/generic/table/Generic'
import DownloadLogicComponent from '@/components/generic/DownloadLogicComponent'

/* Vue */
import { ref, computed } from 'vue'

/* Assets */
import { getDate } from '@/assets/scripts/dateBuilders'
import { amountFormat } from '@/assets/scripts/amounts'
import { titleCaseBuilder, firstCharOfStringUppercase } from '@/assets/scripts/strings'
import { createDownloadInsuranceUrl } from '@/network/api/insurance.api.js'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* mapActions & mapGetters */
const {	getSimulationData, getHolderData } = mapGetters(moduleMapper.BANKASSURANCE_SUBSCRIBE)
const { getMobileAppState } = mapGetters()

/* props received
@formData { Object } - object with info for our current form
*/
const props = defineProps({
    formData: Object
})

const downloadLink = ref(null)
const tableStyles = ref([{ width: '60%', 'min-width': '60%' }, { 'text-align': 'end' }])

const headerData = ref([
    {
        label: t('bankassurance.subscribe.auto.step2.tableHeader.description'),
        dataAttr: 'description',
        showInMobile: true
    }, {
        label: t('bankassurance.subscribe.auto.step2.tableHeader.capital'),
        dataAttr: 'capital',
        showInMobile: true
    }
])

const tableData = computed(() => {
    return getSimulationData.value.quotation.premium.policyRisks.map(ele => {
        return [{
            type: 'title',
            value: ele.descriptive,
            dataAttr: 'description'
        }, {
            type: 'amount',
            value: { content: ele.capital.value, currency: ele.capital.currency, noBalanceIcon: true },
            dataAttr: 'capital'
        }]
    })
})
</script>

<style lang="scss" scoped>
.information-wrapper {
    width: 100%;

    display: flex;
    flex-wrap: wrap;
    grid-row-gap: 24px;

    .text-blocks-wrapper {
        min-width: 220px;
        width: 50%;
    }
}

.mobile-table-wrapper {
    width: 100%;

    .row {
        display: flex;
        flex-direction: column;
        gap: 6px;
        margin-bottom: 16px;
        border-bottom: 1px solid var(--secondary-light-2);
        padding-bottom: 8px;

        &:last-of-type {
            border: none;
            margin-bottom: 0;
        }

        .title-line {
            font-size: 15px;
            line-height: 17px;
            font-weight: 600;
            color: var(--secondary-dark-4);

            margin-left: 16px;
        }

        .data-line {
            font-size: 14px;
            line-height: 17px;
            color: var(--secondary-dark-2);

            margin-left: 16px;
        }
    }

}
</style>
